import { useCallback, useEffect, useState } from "react";
import { AlertCircle, ArrowLeft, RefreshCw, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { PriceAnalysisCard } from "../../components/acquisti/PriceAnalysisCard";
import { PriceAnalysisFilters } from "../../components/acquisti/PriceAnalysisFilters";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const Summary = ({ data }) => {
  const metrics = [
    { key: "aumenti", label: "Aumenti", icon: TrendingUp, color: "text-red-600" },
    { key: "diminuzioni", label: "Cali", icon: TrendingDown, color: "text-emerald-600" },
    { key: "opportunita", label: "Risparmi", icon: Sparkles, color: "text-amber-700" },
  ];
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="price-analysis-summary">
      {metrics.map(({ key, label, icon: Icon, color }) => (
        <div key={key} className="rounded-lg border border-border bg-card px-2 py-2.5 text-center" data-testid={`summary-${key}`}>
          <div className={`flex items-center justify-center gap-1 ${color}`}><Icon className="h-4 w-4" /><span className="text-lg font-extrabold">{data?.[key] || 0}</span></div>
          <p className="text-[10px] font-bold uppercase text-muted-foreground">{label}</p>
        </div>
      ))}
    </div>
  );
};

export default function AnalisiPrezziPage() {
  const navigate = useNavigate();
  const { token, localeCorrente } = useAuth();
  const [scope, setScope] = useState("all");
  const [period, setPeriod] = useState("1m");
  const [supplier, setSupplier] = useState("all");
  const [sort, setSort] = useState("desc");
  const [data, setData] = useState({ items: [], fornitori: [], riepilogo: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    if (!token || !localeCorrente?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      locale_id: localeCorrente.id,
      periodo: period,
      ambito: scope,
      ordinamento: sort,
    });
    if (supplier !== "all") params.set("fornitore_key", supplier);
    try {
      const response = await fetch(`${API}/api/acquisti/analisi-prezzi?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.detail || "Impossibile caricare l'analisi prezzi");
      setData(result);
      if (supplier !== "all" && !result.fornitori.some((item) => item.key === supplier)) setSupplier("all");
    } catch (err) {
      setError(err.message || "Impossibile caricare l'analisi prezzi");
    } finally {
      setLoading(false);
    }
  }, [localeCorrente?.id, period, scope, sort, supplier, token]);

  useEffect(() => { loadData(); }, [loadData]);

  const openHistory = (item) => navigate(`/acquisti/prodotti?tab=${item.tipo === "food" ? "food" : "materiali"}&open=${item.id}`);

  return (
    <div className="mx-auto max-w-6xl pb-24" data-testid="analisi-prezzi-page">
      <header className="sticky top-0 z-30 -mx-4 border-b border-border/80 bg-background/95 px-4 pb-3 pt-1 backdrop-blur-xl sm:mx-0 sm:rounded-b-lg">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("/acquisti/prodotti")} className="h-11 w-11 shrink-0" data-testid="btn-back-prodotti" aria-label="Torna ai prodotti">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-extrabold text-foreground" data-testid="price-analysis-title">Analisi Prezzi</h1>
            <p className="text-xs text-muted-foreground" data-testid="price-analysis-count">{data.riepilogo?.totale || 0} prodotti variati</p>
          </div>
          <Button variant="outline" size="icon" onClick={loadData} disabled={loading} className="h-11 w-11 shrink-0" data-testid="price-analysis-refresh" aria-label="Aggiorna analisi">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <div className="mt-3">
          <PriceAnalysisFilters
            scope={scope} setScope={(value) => { setScope(value); setSupplier("all"); }}
            period={period} setPeriod={(value) => { setPeriod(value); setSupplier("all"); }}
            supplier={supplier} setSupplier={setSupplier}
            suppliers={data.fornitori || []} sort={sort} setSort={setSort}
          />
        </div>
      </header>

      <main className="mt-3 space-y-3">
        <Summary data={data.riepilogo} />
        {loading && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="price-analysis-loading">
            {[0, 1, 2].map((item) => <div key={item} className="h-52 animate-pulse rounded-lg border border-border bg-card" />)}
          </div>
        )}
        {!loading && error && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-5 text-center" data-testid="price-analysis-error">
            <AlertCircle className="mx-auto h-6 w-6 text-red-600" />
            <p className="mt-2 text-sm font-bold text-red-800">{error}</p>
            <Button variant="outline" onClick={loadData} className="mt-3 h-11" data-testid="price-analysis-retry">Riprova</Button>
          </div>
        )}
        {!loading && !error && data.items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center" data-testid="price-analysis-empty">
            <TrendingUp className="mx-auto h-7 w-7 text-muted-foreground" />
            <p className="mt-3 text-base font-extrabold text-foreground">Nessuna variazione nel periodo</p>
            <p className="mt-1 text-sm text-muted-foreground">Prova un periodo più ampio o rimuovi il filtro fornitore.</p>
          </div>
        )}
        {!loading && !error && data.items.length > 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="price-analysis-results">
            {data.items.map((item) => <PriceAnalysisCard key={`${item.tipo}-${item.id}`} item={item} onHistory={openHistory} />)}
          </div>
        )}
      </main>
    </div>
  );
}